// The `surface` PostHog super property: which product surface a browser event
// came from. Guests running classes generate ~82% of event volume (135k/mo of
// $autocapture alone) and drown out the admin app and the paid-ads marketing
// funnel in every default view. Rather than dropping guest capture, every
// client event is stamped with its surface so insights, dashboards, and the
// Activity feed can filter to one audience.
//
// Registration points (all must stay in lockstep with this module):
// - apps/frontend/utils/posthog.ts        — init + pages-router navigations
// - apps/frontend/app/_components/AnalyticsProvider.tsx — App Router navigations
// - apps/frontend/marketing/components/analytics.tsx — always 'marketing'
//
// Server-side events emitted through the Spectra catalog do NOT carry this
// property; a surface filter in PostHog only partitions browser traffic.

export const SURFACE_PROPERTY = 'surface';

export const ANALYTICS_SURFACES = ['guest', 'admin', 'marketing'] as const;

export type AnalyticsSurface = (typeof ANALYTICS_SURFACES)[number];

// First path segments of the frontend app that are NOT church slugs. Anything
// unlisted is a church slug (guest class traffic) — church URLs are unbounded,
// so guest is the default and these sets enumerate the known exceptions.
// Auth pages (login/forgot/reset/join/magic-link) count as admin: guests sign
// in inside their church's own routes, so auth traffic is church staff.
const ADMIN_SEGMENTS = new Set([
  'admin',
  'auth',
  'forgot',
  'internal',
  'join',
  'labs',
  'login',
  'redirect',
  'reset',
]);

// Funnel pages the frontend serves (/welcome-aboard checkout, v2 /start and
// /sign-up) plus the merged marketing routes under app/(marketing)/. Marketing
// registers surface='marketing' itself, but both zones share onark.app — and
// super properties persist per-origin — so the classifier must recognize
// marketing paths wherever it runs.
const MARKETING_SEGMENTS = new Set([
  '',
  'about',
  'build-your-own-class',
  'consulting',
  'contact',
  'cookies',
  'customers',
  'demo',
  'docs',
  'faq',
  'features',
  'free-trial',
  'planning-center',
  'pricing',
  'privacy',
  'refunds',
  'services',
  'sign-up',
  'start',
  'story',
  'terms',
  'vs',
  'welcome-aboard',
  'welcome-aboard-trial',
]);

/** Classify a frontend URL path (with or without query/hash) into the surface
 *  its events belong to. */
export function surfaceForPath(pathname: string): AnalyticsSurface {
  const path = pathname.split(/[?#]/)[0] ?? '';
  const segment = (path.split('/')[1] ?? '').toLowerCase();

  if (ADMIN_SEGMENTS.has(segment)) return 'admin';
  if (MARKETING_SEGMENTS.has(segment)) return 'marketing';

  return 'guest';
}
